import { memo, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { PhoneCall } from "@phosphor-icons/react";
import { responseText, LeadSourceIcon } from "./LeadCells";
import { LeadDetailsButton } from "./LeadDetails";
import { AssignedInlineCell, StatusInlineCell, CardAssignedLine } from "./LeadInlineCells";
import { StatusPill } from "../lux/Primitives";
import { LeadPrimary, LeadActionsMenu, leadPrimaryKind, showConvertInMenu, LEAD_STATUS_TONE } from "./leadActions";
import { formatPhone, telHref } from "../../lib/phone";

// "Sep 9 · 2:34 pm" on ONE line.
const fmtCameIn = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return "—";
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase();
  return `${date} · ${time}`;
};

// Line 2 of "Came in": "No reply yet" (warning) or "Replied in 3h" (muted).
// LEAD-002: a lead the CRM made from a booking (origin "booking") was never a
// lead anyone had to answer, so it reads "Booked directly", muted, instead.
const respLine = (l) => {
  if (l.origin === "booking") return { text: "Booked directly", warn: false };
  // SALES-042: the B squad offer could not be texted (e.g. Quo out of credits)
  // and nobody has reached this lead yet — surface it so it gets picked up.
  if (l.claim_offer?.status === "failed" && !l.first_contacted_at) return { text: "B squad text failed", warn: true };
  if (!l.first_contacted_at) return { text: "No reply yet", warn: true };
  const rt = responseText(l);
  return { text: rt === "—" ? "—" : `Replied in ${rt}`, warn: false };
};

function PhoneLink({ lead, testid }) {
  if (!lead.customer_phone) return <span className="text-muted-foreground">—</span>;
  const href = telHref(lead.customer_phone);
  const display = formatPhone(lead.customer_phone);
  // v2: desktop rows show the number in the foreground colour, never wrapping.
  // 13px (the Came-in date's size — not a new token) keeps a 14-char NA number
  // inside the fixed 136px Phone column with px-3 padding.
  if (!href) return <span className="whitespace-nowrap tabular-nums text-[13px]">{display}</span>;
  return <a href={href} className="whitespace-nowrap tabular-nums text-[13px] text-foreground hover:text-primary hover:underline" data-testid={testid}>{display}</a>;
}

// One table row, memoized so a single-lead action (or revealing one dropdown)
// never re-renders the other rows.
const LeadRow = memo(function LeadRow({
  l, team, selecting, selected, highlight, editingField, isAdmin,
  onStartEdit, onCancelEdit, onCommit, toggleRow,
  onContacted, onConvert, onEdit, onDelete, onOpenBooking,
}) {
  const status = (l.status || "new").toLowerCase();
  const kind = leadPrimaryKind(l);
  const resp = respLine(l);
  return (
    <tr
      className={`border-b border-border/40 transition-colors duration-500 ${highlight ? "bg-primary/10" : "hover:bg-secondary/20"}`}
      data-testid={`lead-row-${l.id}`}
    >
      {selecting && (
        <td className="px-3 py-3 align-top">
          <Checkbox checked={selected} onCheckedChange={() => toggleRow(l.id)} data-testid={`lead-select-${l.id}`} />
        </td>
      )}
      <td className="px-3 py-3">
        <div className="font-heading text-[15px] truncate" title={l.customer_name}>{l.customer_name || "Unnamed lead"}</div>
        <div className="text-xs text-muted-foreground capitalize truncate"><LeadSourceIcon lead={l} testid={`lead-channel-${l.id}`} />{l.lead_no ? <span className="tnum" data-testid={`lead-no-${l.id}`}>{`#${l.lead_no} · `}</span> : null}{l.service_type || "rental"}{l.source ? ` · ${l.source}` : ""}</div>
        <LeadDetailsButton lead={l} onEdit={onEdit} className="mt-1" testid={`lead-details-${l.id}`} />
      </td>
      <td className="px-3 py-3"><PhoneLink lead={l} testid={`lead-phone-${l.id}`} /></td>
      <td className="pl-3 pr-1 py-3" data-testid={`lead-salesperson-${l.id}`}>
        <AssignedInlineCell
          lead={l} team={team} editing={editingField === "assigned"}
          onStartEdit={() => onStartEdit(l.id, "assigned")} onCancelEdit={onCancelEdit}
          onCommit={(v) => onCommit(l.id, "assigned", v)}
        />
      </td>
      <td className="px-3 py-3 whitespace-nowrap" data-testid={`lead-cameIn-${l.id}`}>
        <div className="text-[13px] tabular-nums">{fmtCameIn(l.received_at || l.created_at)}</div>
        <div className={`text-xs ${resp.warn ? "text-amber-400" : "text-muted-foreground"}`} data-testid={`lead-response-${l.id}`}>{resp.text}</div>
      </td>
      <td className="px-3 py-3">
        <StatusInlineCell
          lead={l} editing={editingField === "status"}
          onStartEdit={() => onStartEdit(l.id, "status")} onCancelEdit={onCancelEdit}
          onCommit={(v) => onCommit(l.id, "status", v)}
        />
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center justify-end gap-2">
          <LeadPrimary lead={l} kind={kind} onContacted={onContacted} onConvert={onConvert} onOpenBooking={onOpenBooking} />
          <LeadActionsMenu
            lead={l} isAdmin={isAdmin} showConvert={showConvertInMenu(l, kind)}
            onEdit={onEdit} onConvert={onConvert} onDelete={onDelete}
          />
        </div>
      </td>
    </tr>
  );
});

// One card (phone + tablet), memoized for the same reason.
const LeadCard = memo(function LeadCard({
  l, selecting, selected, highlight, isAdmin, toggleRow,
  onContacted, onConvert, onEdit, onDelete, onOpenBooking,
}) {
  const status = (l.status || "new").toLowerCase();
  const kind = leadPrimaryKind(l);
  return (
    <div
      className={`rounded-2xl border p-4 flex flex-col gap-3 transition-colors duration-500 ${highlight ? "border-primary/40 bg-primary/10" : "border-border bg-card"}`}
      data-testid={`lead-card-${l.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          {selecting && <div className="hidden md:block pt-1"><Checkbox checked={selected} onCheckedChange={() => toggleRow(l.id)} data-testid={`lead-card-select-${l.id}`} /></div>}
          <div className="min-w-0">
            <div className="font-heading text-base truncate" title={l.customer_name}>{l.customer_name || "Unnamed lead"}</div>
            <div className="text-xs text-muted-foreground truncate capitalize">
              <LeadSourceIcon lead={l} testid={`lead-card-channel-${l.id}`} />{l.lead_no ? <span className="tnum" data-testid={`lead-card-no-${l.id}`}>{`#${l.lead_no} · `}</span> : null}{l.service_type || "rental"}{l.source ? ` · ${l.source}` : ""}
            </div>
            <LeadDetailsButton lead={l} onEdit={onEdit} className="mt-1 min-h-[32px]" testid={`lead-card-details-${l.id}`} />
          </div>
        </div>
        <StatusPill status={status} tone={LEAD_STATUS_TONE[status]} className="shrink-0" />
      </div>
      <div className="flex items-center justify-between gap-3 text-sm">
        {l.customer_phone && telHref(l.customer_phone)
          ? <a href={telHref(l.customer_phone)} className="inline-flex shrink-0 min-h-[44px] items-center gap-1.5 text-primary" data-testid={`lead-phone-${l.id}`}><PhoneCall size={14} />{formatPhone(l.customer_phone)}</a>
          : <span className="shrink-0 text-muted-foreground">{formatPhone(l.customer_phone) || "—"}</span>}
        <div className="flex flex-col items-end min-w-0">
          <CardAssignedLine lead={l} testid={`lead-card-assigned-${l.id}`} />
          <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtCameIn(l.received_at || l.created_at)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <LeadPrimary lead={l} kind={kind} touch onContacted={onContacted} onConvert={onConvert} onOpenBooking={onOpenBooking} />
        <LeadActionsMenu
          lead={l} isAdmin={isAdmin} touch showConvert={showConvertInMenu(l, kind)}
          onEdit={onEdit} onConvert={onConvert} onDelete={onDelete}
        />
      </div>
    </div>
  );
});

// The whole table is memoized. Because none of the lead-dialog's form state is
// passed in, typing in the dialog re-renders the page but skips this subtree.
// SALES-032: seven columns, no min-width (never scrolls sideways), and a card
// layout below the `xl` breakpoint (where the table would not fit beside the
// 256px sidebar).
export const AllLeadsTable = memo(function AllLeadsTable({
  rows, totalMatching, shownCount, hasMore, onLoadMore, loading, isError,
  team, selecting, selected, allSelected, toggleRow, toggleAll, isAdmin,
  editingCell, onStartEdit, onCancelEdit, onCommit,
  onContacted, onConvert, onEdit, onDelete, onOpenBooking, highlightId,
}) {
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore || loading) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) onLoadMore(); }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, onLoadMore, shownCount]);

  const emptyMsg = isError ? "Unable to load leads." : loading ? "Loading…" : totalMatching === 0 ? "No leads match these filters." : null;
  const emptyTone = isError ? "text-destructive" : "text-muted-foreground";

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <h3 className="font-heading text-lg">All leads</h3>
        <span className="tnum text-sm text-muted-foreground" data-testid="leads-count">{totalMatching}</span>
      </div>

      {/* Desktop table (xl and up) */}
      <div className="hidden xl:block border border-border bg-card">
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className="border-b border-border">
              {selecting && (
                <th className="w-10 px-3 py-3">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="leads-select-all" />
                </th>
              )}
              <th className="text-left px-3 py-3 label-mono font-normal">Lead</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[136px]">Phone</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[124px]">Assigned</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[136px]">Came in</th>
              <th className="text-left px-3 py-3 label-mono font-normal w-[142px]">Status</th>
              <th className="text-right px-3 py-3 label-mono font-normal w-[200px]">Next step</th>
            </tr>
          </thead>
          <tbody>
            {emptyMsg ? <tr><td colSpan={selecting ? 7 : 6} className={`p-10 text-center ${emptyTone}`}>{emptyMsg}</td></tr>
              : rows.map((l) => (
                <LeadRow
                  key={l.id} l={l} team={team} isAdmin={isAdmin}
                  selecting={selecting}
                  selected={selected.includes(l.id)}
                  highlight={highlightId === l.id}
                  editingField={editingCell?.id === l.id ? editingCell.field : null}
                  onStartEdit={onStartEdit} onCancelEdit={onCancelEdit} onCommit={onCommit}
                  toggleRow={toggleRow}
                  onContacted={onContacted} onConvert={onConvert} onEdit={onEdit} onDelete={onDelete} onOpenBooking={onOpenBooking}
                />
              ))}
          </tbody>
        </table>
      </div>

      {/* Phone + tablet cards (below xl) */}
      <div className="xl:hidden">
        {emptyMsg ? <div className={`border border-border bg-card rounded-2xl p-10 text-center ${emptyTone}`}>{emptyMsg}</div>
          : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {rows.map((l) => (
                <LeadCard
                  key={l.id} l={l} isAdmin={isAdmin}
                  selecting={selecting}
                  selected={selected.includes(l.id)}
                  highlight={highlightId === l.id}
                  toggleRow={toggleRow}
                  onContacted={onContacted} onConvert={onConvert} onEdit={onEdit} onDelete={onDelete} onOpenBooking={onOpenBooking}
                />
              ))}
            </div>
          )}
      </div>

      {!loading && !isError && totalMatching > 0 && (
        <div className="flex items-center justify-center gap-4 py-4">
          <span className="text-xs text-muted-foreground tnum" data-testid="leads-showing">Showing {shownCount} of {totalMatching}</span>
          {hasMore && (
            <Button variant="outline" size="sm" className="rounded-full" onClick={onLoadMore} data-testid="leads-load-more">Load 50 more</Button>
          )}
        </div>
      )}
      <div ref={sentinelRef} aria-hidden="true" />
    </div>
  );
});
